import React, { useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Lock } from "lucide-react";
import LogoLink from "./LogoLink.jsx";
import {
  isValidTicker,
  normalizeTicker,
  extractUtmString,
  savePendingDestination,
} from "./ffvDestination.js";

// Landing page for the SEO site's CTA links (tickrtrends.com/ffv/{TICKER}).
// Authenticated visitors go straight to the members page with the ticker
// pre-filled; everyone else gets a focused sign-in/sign-up prompt, with the
// destination stashed in sessionStorage so it survives the Cognito redirect.
const FfvLanding = () => {
  const { ticker: rawTicker } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();

  const isValid = isValidTicker(rawTicker);
  const ticker = isValid ? normalizeTicker(rawTicker) : null;
  const utm = extractUtmString(window.location.search);

  useEffect(() => {
    if (!isValid || auth.isLoading || !auth.isAuthenticated) return;
    // Backstop in case the members page bounces through Cognito again
    savePendingDestination({ ticker, utm });
    const query = new URLSearchParams(utm);
    query.set("ticker", ticker);
    navigate(`/members?${query.toString()}`, { replace: true });
  }, [isValid, ticker, utm, auth.isLoading, auth.isAuthenticated, navigate]);

  if (!isValid) {
    return <Navigate to="/" replace />;
  }

  const handleSignIn = () => {
    savePendingDestination({ ticker, utm });
    auth.signinRedirect();
  };

  const handleSignUp = () => {
    savePendingDestination({ ticker, utm });
    navigate(`/signup${utm ? `?${utm}` : ""}`);
  };

  if (auth.isLoading || auth.isAuthenticated) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: "center", color: "#8a94a2" }}>
          <div className="loading-spinner"></div>
          <p style={{ marginTop: "16px" }}>Loading {ticker}...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <LogoLink className="logo-link--card" />

        <div style={styles.lockCircle}>
          <Lock size={22} style={{ color: "#8b5cf6" }} />
        </div>

        <h1 style={styles.headline}>
          Sign in to see <span style={{ color: "#8b5cf6" }}>{ticker}</span>&apos;s exact
          Forward Fair Value
        </h1>

        <p style={styles.subtitle}>
          Members get forward fair value, quality scores, DCF valuations, and
          momentum signals for 500+ stocks — updated weekly.
        </p>

        <button style={styles.primaryButton} onClick={handleSignIn}>
          Sign In
        </button>
        <button style={styles.secondaryButton} onClick={handleSignUp}>
          Start Free Trial — $4.99/month
        </button>

        <a href={`/${utm ? `?${utm}` : ""}`} style={styles.homeLink}>
          Learn more about TickrTrends →
        </a>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(180deg, #0f1419 0%, #151923 100%)",
    padding: "24px",
  },
  card: {
    maxWidth: "440px",
    width: "100%",
    background: "#1a1f2e",
    border: "1px solid rgba(139, 92, 246, 0.3)",
    borderRadius: "12px",
    padding: "40px 32px",
    textAlign: "center",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
  },
  lockCircle: {
    width: "56px",
    height: "56px",
    margin: "0 auto 20px",
    borderRadius: "50%",
    background: "rgba(139, 92, 246, 0.12)",
    border: "1px solid rgba(139, 92, 246, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headline: {
    color: "#ffffff",
    fontSize: "1.35rem",
    fontWeight: "600",
    lineHeight: "1.4",
    marginBottom: "12px",
  },
  subtitle: {
    color: "#8a94a2",
    fontSize: "0.95rem",
    lineHeight: "1.6",
    marginBottom: "28px",
  },
  primaryButton: {
    width: "100%",
    padding: "13px",
    background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
    border: "none",
    borderRadius: "8px",
    color: "#ffffff",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "12px",
  },
  secondaryButton: {
    width: "100%",
    padding: "13px",
    background: "transparent",
    border: "1px solid rgba(139, 92, 246, 0.4)",
    borderRadius: "8px",
    color: "#a78bfa",
    fontSize: "0.95rem",
    fontWeight: "500",
    cursor: "pointer",
    marginBottom: "20px",
  },
  homeLink: {
    color: "#8a94a2",
    fontSize: "0.85rem",
    textDecoration: "none",
  },
};

export default FfvLanding;
